import { actions, overlays } from '@ecosystem/newron-design-system';
import {
  DELETE_GROUP_PG_ELEMENT_NAME,
  GROUP_ELEMENT_TYPE,
  GroupContext,
  Preference,
  PreferenceResponseStatus,
  UNKNOWN_ERROR_MESSAGE,
} from '@utils/userPreferencesUtils/Types';
import { buildProductNamespace, useProductAnalyzerGroupState } from '@utils/hook/userPreferences/UseGroupState';
import { useProductAnalyzerGroupEventState } from '@components/top-selling-products/product-table-grouping/utils/ProductAnalyzerGroupContext';
import { useSelector } from 'react-redux';
import React, { useState } from 'react';
import {
  groupElementClickedTrackingFunction,
  PRODUCT_LEADERBOARD_TABLE_ELEMENT_CLICKED_TITLE,
  PRODUCT_LEADERBOARD_TABLE_NAME,
  TableElementClickedParam,
} from '@utils/page-analytics/model/GroupElementClickTracking';
import { PageName, PageType } from '@utils/pageConfig/Types';
import { authentication, useAnalytics } from '@ecosystem/newron-client-lib';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';

export interface Props {
  context: GroupContext;
}

export const DeleteProductLeaderboardModalGroup: React.FC<Props> = ({ context }) => {
  const { getProductAnalyzerGroup, deleteProductAnalyzerGroup, deletePreferencePermissionProductLeaderboardGroup } =
    useProductAnalyzerGroupState(context);
  const { groupNameToBeDelete, setGroupNameToBeDelete, isDeleteModalGroupPopUp, setIsDeleteModalGroupPopUp } =
    useProductAnalyzerGroupEventState();
  const sideFiltersState = useSelector((state: any) => state.sideFilters);

  const [error, setError] = useState<string>('');
  const [isDeletingGroup, setIsDeletingGroup] = useState<boolean>(false);
  const { track } = useAnalytics();
  const pagesConfig = usePageConfig();

  const handleCancelDelete = () => {
    setError('');
    setIsDeletingGroup(false);
    setIsDeleteModalGroupPopUp(false);
    setGroupNameToBeDelete('');
  };

  const getFeatureSpecificQuery = (type: 'delete' | 'get' | 'deletePermission') => {
    const preferenceQueryMap = {
      delete: deleteProductAnalyzerGroup,
      get: getProductAnalyzerGroup,
      deletePermission: deletePreferencePermissionProductLeaderboardGroup,
    };

    return preferenceQueryMap[type];
  };

  const handleDeleteGroup = () => {
    const namespace = buildProductNamespace(context);
    const userId = authentication.getUser()?.userId ?? '';

    //Amplitude Tracking when Delete group button is click
    const trackingParam: TableElementClickedParam = {
      eventTitle: PRODUCT_LEADERBOARD_TABLE_ELEMENT_CLICKED_TITLE,
      elementName: DELETE_GROUP_PG_ELEMENT_NAME.model,
      elementType: GROUP_ELEMENT_TYPE.model,
      track: track,
      optionChosen: groupNameToBeDelete,
      pageName: PageName.RI_PRODUCT,
      pageType: PageType.RI_PRODUCT,
      sideFilters: sideFiltersState.appliedFilters,
      tableName: PRODUCT_LEADERBOARD_TABLE_NAME,
      access: pagesConfig.access,
    };

    const deleteGroupQuery = getFeatureSpecificQuery('delete');
    const getGroupQuery = getFeatureSpecificQuery('get');
    const deletePermissionQuery = getFeatureSpecificQuery('deletePermission');

    groupElementClickedTrackingFunction(trackingParam);

    setError('');
    setIsDeletingGroup(true);

    // Retrieve current preference
    const data = getGroupQuery.data as Preference<any>[];
    const currentPreference = data.find(
      (preference) => preference.namespace === namespace && preference.key === groupNameToBeDelete,
    );

    // Reusable mutation handler
    const handleMutation = (query: any, variables: any, onSuccessCallback: () => void) => {
      query.mutate(variables, {
        onSuccess: (response) => {
          setIsDeletingGroup(false);

          if (response.status === PreferenceResponseStatus.SUCCESS) {
            onSuccessCallback();
          } else {
            setError(response.message);
          }
        },
        onError: () => {
          setIsDeletingGroup(false);
          setError(UNKNOWN_ERROR_MESSAGE);
        },
      });
    };

    // If the user is the owner, delete the group
    if (currentPreference?.isPreferenceOwner) {
      handleMutation(deleteGroupQuery, { key: groupNameToBeDelete }, () => handleCancelDelete());
      return;
    }

    // Delete preference permission regardless of ownership
    handleMutation(deletePermissionQuery, { key: groupNameToBeDelete, userId }, () => handleCancelDelete());
  };

  const { Modal } = overlays;
  const { Button } = actions;
  return (
    <Modal isOpen={isDeleteModalGroupPopUp} onClose={handleCancelDelete}>
      <Modal.Header>
        <h2>Delete Product Leaderboard Group</h2>
      </Modal.Header>
      <Modal.Body>
        <p>Are you sure you want to delete “{groupNameToBeDelete}” group?</p>
        {error && <span className="Input__error">{error}</span>}
      </Modal.Body>
      <Modal.Footer>
        <Button mode="secondary" disabled={isDeletingGroup} onClick={handleCancelDelete}>
          Cancel
        </Button>
        <Button onClick={handleDeleteGroup} disabled={isDeletingGroup}>
          {isDeletingGroup ? 'Deleting...' : 'Yes, delete group'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
